import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, ShoppingCartIcon, TrendingUpIcon, BarChart3Icon } from 'lucide-react';
export function HeroSection() {
  return <section className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden bg-gradient-to-b from-gray-50/50 to-white" data-id="element-114">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-115">
        <div className="lg:grid lg:grid-cols-12 lg:gap-16 items-center" data-id="element-116">
          {/* Text Content */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="lg:col-span-6 text-center lg:text-left mb-16 lg:mb-0" data-id="element-117">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold font-heading text-navy leading-[1.15] mb-6" data-id="element-118">
              We Build & Grow <br className="hidden sm:block" data-id="element-119" />
              <span className="text-teal" data-id="element-120">Ecommerce Brands</span>
            </h1>

            <p className="text-lg sm:text-xl font-medium text-gray-800 mb-4" data-id="element-121">
              WooCommerce, Shopify & custom ecommerce development + digital
              marketing for fashion, beauty & lifestyle brands.
            </p>

            <p className="text-base sm:text-lg text-gray-600 mb-8 max-w-2xl mx-auto lg:mx-0" data-id="element-122">
              From store builds and platform migrations to SEO that triples your
              traffic — we help DTC brands sell more online. US-registered.
              Globally delivered. Obsessively focused on your growth.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start space-y-4 sm:space-y-0 sm:space-x-6" data-id="element-123">
              <a href="https://calendly.com/fahad-3techno/strategy-call" target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-4 text-base font-semibold text-white bg-navy rounded-xl hover:bg-navy-light transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5" data-id="element-124">
                Get a Free Store Audit
              </a>
              <a href="#our-work" className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-4 text-base font-semibold text-navy bg-white border-2 border-gray-100 rounded-xl hover:border-teal hover:text-teal transition-colors" data-id="element-125">
                See Our Work
                <ArrowRightIcon className="ml-2 w-5 h-5" data-id="element-126" />
              </a>
            </div>
          </motion.div>

          {/* Visual / Mockup */}
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="lg:col-span-6 relative" data-id="element-127">
            {/* Decorative background blob */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-teal/5 rounded-full blur-3xl -z-10" data-id="element-128"></div>

            {/* Abstract Laptop/Dashboard Mockup */}
            <div className="relative mx-auto w-full max-w-lg rounded-2xl bg-white shadow-2xl border border-gray-100 overflow-hidden" data-id="element-129">
              {/* Browser Header */}
              <div className="bg-gray-50 border-b border-gray-100 px-4 py-3 flex items-center space-x-2" data-id="element-130">
                <div className="flex space-x-1.5" data-id="element-131">
                  <div className="w-3 h-3 rounded-full bg-red-400" data-id="element-132"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-400" data-id="element-133"></div>
                  <div className="w-3 h-3 rounded-full bg-green-400" data-id="element-134"></div>
                </div>
                <div className="ml-4 flex-1 bg-white rounded-md h-6 border border-gray-200 flex items-center px-3" data-id="element-135">
                  <div className="w-32 h-2 bg-gray-200 rounded-full" data-id="element-136"></div>
                </div>
              </div>

              {/* Mockup Body */}
              <div className="p-6 bg-gray-50/50" data-id="element-137">
                <div className="flex justify-between items-center mb-6" data-id="element-138">
                  <div data-id="element-139">
                    <div className="h-4 w-24 bg-gray-200 rounded-full mb-2" data-id="element-140"></div>
                    <div className="h-8 w-32 bg-navy rounded-md" data-id="element-141"></div>
                  </div>
                  <div className="w-10 h-10 rounded-full bg-teal/10 flex items-center justify-center text-teal" data-id="element-142">
                    <ShoppingCartIcon size={20} data-id="element-143" />
                  </div>
                </div>

                {/* Chart Mockup */}
                <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm mb-6" data-id="element-144">
                  <div className="flex items-center justify-between mb-4" data-id="element-145">
                    <div className="h-3 w-20 bg-gray-200 rounded-full" data-id="element-146"></div>
                    <div className="flex items-center text-success text-sm font-medium" data-id="element-147">
                      <TrendingUpIcon size={14} className="mr-1" data-id="element-148" />
                      +24.5%
                    </div>
                  </div>
                  <div className="h-32 flex items-end space-x-2" data-id="element-149">
                    {[40, 70, 45, 90, 65, 110, 85].map((height, i) => <motion.div key={i} initial={{
                    height: 0
                  }} animate={{
                    height: `${height}%`
                  }} transition={{
                    duration: 1,
                    delay: 0.5 + i * 0.1
                  }} className="flex-1 bg-teal rounded-t-sm opacity-80" data-id="element-150"></motion.div>)}
                  </div>
                </div>

                {/* Mini Cards */}
                <div className="grid grid-cols-2 gap-4" data-id="element-151">
                  <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm flex items-center space-x-3" data-id="element-152">
                    <div className="w-8 h-8 rounded-lg bg-navy/10 flex items-center justify-center text-navy" data-id="element-153">
                      <BarChart3Icon size={16} data-id="element-154" />
                    </div>
                    <div data-id="element-155">
                      <div className="h-2 w-12 bg-gray-200 rounded-full mb-1.5" data-id="element-156"></div>
                      <div className="h-3 w-16 bg-gray-800 rounded-full" data-id="element-157"></div>
                    </div>
                  </div>
                  <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm flex items-center space-x-3" data-id="element-158">
                    <div className="w-8 h-8 rounded-lg bg-success/10 flex items-center justify-center text-success" data-id="element-159">
                      <TrendingUpIcon size={16} data-id="element-160" />
                    </div>
                    <div data-id="element-161">
                      <div className="h-2 w-12 bg-gray-200 rounded-full mb-1.5" data-id="element-162"></div>
                      <div className="h-3 w-16 bg-gray-800 rounded-full" data-id="element-163"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Floating Badge */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.8
          }} className="absolute -bottom-6 -left-6 bg-white p-4 rounded-xl shadow-xl border border-gray-100 flex items-center space-x-3 z-10 hidden sm:flex" data-id="element-164">
              <div className="w-12 h-12 rounded-full bg-success/10 flex items-center justify-center text-success" data-id="element-165">
                <TrendingUpIcon size={24} data-id="element-166" />
              </div>
              <div data-id="element-167">
                <p className="text-sm font-bold text-gray-900" data-id="element-168">
                  3x Traffic Growth
                </p>
                <p className="text-xs text-gray-500" data-id="element-169">Average client result</p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>;
}